/**
 * runRecordInputs — the small derivations every host needs to build a
 * `RunRecordBodyProps` from a run.
 *
 * These started as page-local helpers in `routes/workflows.runs.$instanceId`
 * with a comment saying they were "not worth a shared module for one call
 * site". There are three call sites now — the route, ora's drawer and the
 * add-in's — and the record is supposed to read identically in all of them,
 * so a per-host copy of "where does the client name come from" is exactly the
 * drift this series exists to remove.
 */

/** "carrier_mortgagee_update_v2" -> "Carrier Mortgagee Update V2". */
export function titleCaseId(id: string | null | undefined): string | null {
  if (!id) return null;
  return id.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Best-effort read of a metadata field the typed status response doesn't
 * declare. Returns `null`, never a fabricated value, when absent —
 * `RunSection`'s `InstanceField` already renders an em-dash for `null`.
 */
export function readMetadataString(
  metadata: Record<string, unknown> | null | undefined,
  keys: string[],
): string | null {
  if (!metadata) return null;
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim().length > 0) return value.trim();
  }
  return null;
}

/**
 * Client and policy identity, read from `metadata.client_context`.
 *
 * This is the ONE place that knows the shape. A host reading it from its own
 * grid row instead would silently disagree with the record page whenever the
 * row is stale or the drawer was opened from a notification (which carries an
 * instance id and nothing else).
 */
export function readClientContext(
  metadata: Record<string, unknown> | null | undefined,
): { clientName: string | null; policyNumber: string | null } {
  const clientContext =
    metadata &&
    typeof metadata.client_context === "object" &&
    metadata.client_context !== null
      ? (metadata.client_context as Record<string, unknown>)
      : null;
  const client =
    clientContext &&
    typeof clientContext.client === "object" &&
    clientContext.client !== null
      ? (clientContext.client as Record<string, unknown>)
      : null;
  const policy =
    clientContext &&
    typeof clientContext.policy === "object" &&
    clientContext.policy !== null
      ? (clientContext.policy as Record<string, unknown>)
      : null;
  return {
    clientName: typeof client?.name === "string" ? client.name : null,
    policyNumber: typeof policy?.number === "string" ? policy.number : null,
  };
}
